import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MonitorPlay, VideoCamera, WifiHigh, WifiSlash, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Streaming() {
  const [config, setConfig] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStream, setActiveStream] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, camerasRes] = await Promise.all([
        api.get("/streaming/config"),
        api.get("/streaming/cameras"),
      ]);
      setConfig(configRes.data);
      setCameras(camerasRes.data.cameras || []);
    } catch {
      toast.error("Error al cargar streaming");
    } finally {
      setLoading(false);
    }
  };

  const openStream = (cameraId) => {
    setActiveStream(cameraId);
    const token = localStorage.getItem("padel_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    setStreamUrl(`${base}/api/streaming/view/${cameraId}?token=${encodeURIComponent(token)}`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="streaming-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">En Vivo</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Streaming</h1>
        </div>
        <Button onClick={loadData} variant="outline" className="border-white/20 text-gray-300 hover:border-[#CCFF00] hover:text-[#CCFF00] text-xs uppercase font-bold" data-testid="refresh-streaming-btn">
          <ArrowClockwise size={16} className="mr-2" /> Actualizar
        </Button>
      </div>

      {/* Connection Status */}
      <div className="card-tactical p-5 flex items-center justify-between" data-testid="streaming-status">
        <div className="flex items-center gap-3">
          {config?.connected ? (
            <WifiHigh size={20} className="text-green-400" />
          ) : (
            <WifiSlash size={20} className="text-red-400" />
          )}
          <div>
            <p className="text-sm font-semibold text-white">
              Synology Surveillance Station
            </p>
            <p className="text-xs text-gray-500">
              Servidor: {config?.server || "No configurado"}
            </p>
          </div>
        </div>
        <Badge variant="outline" className={`badge-status ${config?.connected ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}`}>
          {config?.connected ? "Conectado" : "Desconectado"}
        </Badge>
      </div>

      {/* Stream Player */}
      {activeStream && (
        <div className="relative bg-black border border-white/10 aspect-video w-full overflow-hidden group" data-testid="stream-player">
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <div className="live-dot" />
            <Badge className="bg-[#CCFF00] text-black font-bold text-xs uppercase">EN VIVO</Badge>
          </div>
          {streamUrl ? (
            <img
              src={streamUrl}
              alt="Live Stream"
              className="w-full h-full object-cover"
              data-testid="stream-video"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MonitorPlay size={64} weight="duotone" className="text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500">Conectando al stream...</p>
              </div>
            </div>
          )}
          <Button
            onClick={() => { setActiveStream(null); setStreamUrl(null); }}
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/80"
            data-testid="close-stream-btn"
          >
            Cerrar
          </Button>
        </div>
      )}

      {/* Camera List */}
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-4">Camaras Disponibles</p>
        {cameras.length === 0 ? (
          <div className="card-tactical p-12 text-center">
            <VideoCamera size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">No hay camaras disponibles</p>
            <p className="text-xs text-gray-600">
              {config?.configured
                ? "Verifica la conexion con tu servidor Synology"
                : "Configura tu servidor Synology para habilitar el streaming"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cameras.map((cam) => (
              <div key={cam.id} className="card-tactical p-5 group cursor-pointer" onClick={() => openStream(cam.id)} data-testid={`camera-card-${cam.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <VideoCamera size={24} weight="duotone" className="text-[#CCFF00]" />
                    <div>
                      <p className="font-semibold text-white text-sm">{cam.name}</p>
                      <p className="text-xs text-gray-500">ID: {cam.id}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={`badge-status ${cam.status === 1 ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-gray-500/20 text-gray-400 border-gray-500/30"}`}>
                    {cam.status === 1 ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-[#141414] border border-white/5 p-5 text-sm text-gray-500" data-testid="streaming-info">
        <p className="font-['Barlow_Condensed'] text-xs uppercase tracking-[0.2em] font-semibold text-gray-400 mb-2">Informacion</p>
        <p>El streaming en vivo utiliza Synology Surveillance Station via QuickConnect. Asegurate de que tu NAS este encendido y conectado a internet para visualizar las camaras.</p>
      </div>
    </div>
  );
}
